/* eslint-disable react/jsx-no-useless-fragment */
import { generateId, memoizeFunction } from '@agilepoint/utils-common';
import * as React from 'react';
import styled from 'styled-components';
import { IFileUploadProps, IFileInfo } from './file-upload.types';
import { getAllFilesInfo, useFileRepository } from './utils';
import { FilePreview } from './parts/file-preview';
import { FlexBox } from '../flex-box';
import { selectThemeSemantics } from '../../styling';
import { StackPanel } from '../stack-panel';
import { Illustration } from '../illustration';
import { Typography } from '../typography';
import { PrimaryButton } from '../button';
import { Separator } from '../separator';

const IMAGE_EXTENSION_REGEX = /^image\//i;

const DefaultFileInput = styled.input.attrs({
    type: 'file'
})`
    display: none;
`;

const parseFileInfo = (settledResult: PromiseSettledResult<IFileInfo>): IFileInfo => (settledResult.status === 'fulfilled' ? settledResult.value : settledResult.reason);

const FlexBoxWithBorder = styled(FlexBox).attrs({
    className: String('ap-fileUpload-field')
})`
    width: 100%;
    border-style: dashed;
    flex-shrink: 0;
    height: 140px;
    border-width: ${({ theme: { fileUpload: { composites } } }) => composites.borderWidth};
    border-color: ${({ theme: { fileUpload: { colors } } }) => colors.borderColor};
    border-radius: ${({ theme: { fileUpload: { composites } } }) => composites.borderRadius};
`;

const Wrapper = styled(StackPanel).attrs({
    className: String('ap-fileUpload-wrapper')
})`
    height: 100%;
    width: 100%;
    min-width: 250px;
    box-sizing: border-box;
    background-color: ${({ theme: { fileUpload: { colors } } }) => colors.backgroundColor};
`;

const Placeholder = styled(Typography.Small).attrs({
    className: String('ap-fileUpload-placeholder')
})`
    color: ${({ theme: { fileUpload: { colors } } }) => colors.foregroundColor};
`;

const stopEvent = (ev: React.SyntheticEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
};

const StyledHorizontalSeparator = styled(Separator)`
    position: static;
    width: 100%;
    background-color: ${selectThemeSemantics(({ palette }) => palette.neutralTertiary)};
`;

const onRenderSeperator = () => (
    <StyledHorizontalSeparator hideBeak orientation="horizontal" />
);

const trim = (extension: string) => extension.replace('.', '').trim();
const generateAcceptedTypeRegex = memoizeFunction((extensions: string) => new RegExp(`.(${extensions.split(',').map(trim).join('|')})$`, 'i'));

const filterValidFiles = (files: FileList, accept = '*'): File[] => Array.from(files).filter(file => {
    if (accept === '*') {
        return true;
    }
    if (IMAGE_EXTENSION_REGEX.test(accept) && IMAGE_EXTENSION_REGEX.test(file.type)) {
        return true;
    }
    const acceptedTypesRegex = generateAcceptedTypeRegex(accept);
    return acceptedTypesRegex.test(file.name);
});

export const FileUpload = React.memo((props: IFileUploadProps) => {
    const {
        elementRef, className, hidden, label = 'Upload File', placeholder = 'Drag and drop or',
        accept, multiple, initialSelectedFiles, readMode = 'readAsText', onRenderItemActions: onRenderActions,
        onRenderItemLabel: onRenderLabel, onRenderItemTile: onRenderTile, onChange
    } = props;
    const id = React.useMemo(() => generateId(), []);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const { selectedFiles, remove, upsert } = useFileRepository(initialSelectedFiles, multiple, onChange);

    const _onButtonClick = React.useCallback(() => {
        const { current } = inputRef;
        current?.click();
    }, []);

    const renderPreviewItem = React.useCallback((info: IFileInfo) => (
        <FilePreview {...info} key={info.name} onRenderItemTile={onRenderTile} onRemove={remove}
            onRenderItemActions={onRenderActions} onRenderItemLabel={onRenderLabel} />
    ), [onRenderActions, onRenderLabel, onRenderTile, remove]);

    const loadSelectedFiles = React.useCallback(async (fileList: File[] | null) => {
        if (!fileList || fileList.length === 0) {
            return;
        }
        const result = (await getAllFilesInfo(fileList, readMode)).map(parseFileInfo);
        upsert(result);
        const { current } = inputRef;
        if (!current) {
            return;
        }
        current.value = '';
    }, [readMode, upsert]);

    const _onFileSelect = React.useCallback((ev: React.ChangeEvent<HTMLInputElement>) => {
        loadSelectedFiles(ev.target.files ? Array.from(ev.target.files) : null);
    }, [loadSelectedFiles]);

    const _onDropFiles = React.useCallback((ev: React.DragEvent) => {
        const fileList = filterValidFiles(ev.dataTransfer.files, accept);
        stopEvent(ev);
        if (fileList.length === 0) {
            return;
        }
        loadSelectedFiles(fileList);
    }, [loadSelectedFiles, accept]);

    return hidden ? null : (
        <Wrapper gap="15" className={className} ref={elementRef} orientation="vertical" alignment="center">
            <FlexBoxWithBorder justifyContent="center" alignItems="center" onDrop={_onDropFiles}
                onDragEnter={stopEvent} onDragOver={stopEvent} onDragLeave={stopEvent}>
                <StackPanel gap="25" alignment="center">
                    <Illustration type="ap-image-illustration" height="60" width="60" />
                    <label htmlFor={id}>
                        <StackPanel orientation="vertical" gap="10">
                            <DefaultFileInput id={id} type="file" ref={inputRef} accept={accept} multiple={multiple} onChange={_onFileSelect} />
                            <Placeholder>{placeholder}</Placeholder>
                            <PrimaryButton type="button" label={label} size="small" onClick={_onButtonClick} />
                        </StackPanel>
                    </label>
                </StackPanel>
            </FlexBoxWithBorder>
            <React.Fragment>
                {selectedFiles.length ? (
                    <StackPanel className="ap-fileUpload-previewContainer" width="100%" orientation="vertical" gap="16" separator={onRenderSeperator}>
                        <>{selectedFiles.map(renderPreviewItem)}</>
                        <span />
                    </StackPanel>
                ) : null}
            </React.Fragment>
        </Wrapper>
    );
});

FileUpload.displayName = 'FileUpload';
